import Text from "common/Typography/Text";
import { CSSProperties, useState } from "react";
import Modal from "common/Modal/Modal";
import styled from "styled-components";
import { typography } from "@shared/common/Themes/typography";
import {
  ErrorTypes,
  ErrorTypeToDisplay,
  OrderTypes,
} from "@shared/features/trades/common/types";
import { getErrorDisplayMessage } from "@shared/features/trades/common/utils";
import { AnimatePresence, motion } from "motion/react";
import { getResolveWarningMessage } from "@shared/features/tradingTerminal/common/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface OrderSectionErrorsSummaryProps {
  errors: ErrorTypeToDisplay;
  orderType: OrderTypes;
  resolveIssues?: () => void;
  isSignal?: boolean;
  style?: CSSProperties;
  errorsExtraData?: ErrorTypeToDisplay;
}

const OrderSectionErrorsSummary = ({
  errors,
  orderType,
  resolveIssues,
  isSignal,
  style,
  errorsExtraData,
}: OrderSectionErrorsSummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);

  const closeModal = () => setResolveModalOpen(false);

  const onResolveIssues = () => {
    if (resolveIssues) {
      resolveIssues();
      closeModal();
    }
  };

  return (
    <div style={style}>
      <div style={{ paddingBottom: 12 }}>
        <AnimatePresence>
          {Object.keys(errors).map((error, index) => {
            const errorType = parseInt(error) as unknown as ErrorTypes;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ ease: "linear", duration: 0.35 }}
                style={{ display: "flex", alignItems: "baseline" }}
              >
                <ErrorMessage>
                  {Object.keys(errors).length > 1 && <span>{index + 1}. </span>}
                  {getErrorDisplayMessage(
                    t,
                    errorType,
                    orderType,
                    isSignal
                  )}{" "}
                  {errorsExtraData?.[error]}
                </ErrorMessage>
                {errorType === ErrorTypes.SUM_OF_RATIOS && !!resolveIssues && (
                  <ResolveButton
                    type="danger"
                    onClick={() => setResolveModalOpen(true)}
                  >
                    {t("labels.resolve")}
                  </ResolveButton>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      <Modal
        onOk={onResolveIssues}
        onCancel={closeModal}
        open={resolveModalOpen}
        destroyOnHidden={true}
        okText={t("labels.resolve")}
        title={t("labels.resolve_issue")}
      >
        <Text>{getResolveWarningMessage(orderType, isSignal)}</Text>
      </Modal>
    </div>
  );
};

export default OrderSectionErrorsSummary;

const ErrorMessage = styled(Text).attrs({
  type: "danger",
  $size: typography.fontSizes.S,
  as: "div",
})`
  border-left: 2px solid ${(p) => p.theme.colors.red};
  padding: 4px 0 0 12px;
`;
const ResolveButton = styled(Text)`
  padding-left: 8px;
  cursor: pointer;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  text-decoration: underline;
`;
